import { type LicenseData, useLicenseStore } from "@/hooks/useLicenseStore"
import { useCallback, useEffect, useRef } from "react"
import { type FieldValues, type UseFormProps, useForm } from "react-hook-form"

const serialize = (value: unknown) => {
  try {
    return JSON.stringify(value)
  } catch (error) {
    console.warn("Failed to serialize form values", error)
    return ""
  }
}

type UseBuilderFormOptions<TFieldValues extends FieldValues> = UseFormProps<TFieldValues>

export const useBuilderForm = <TFieldValues extends FieldValues = LicenseData>(
  options: UseBuilderFormOptions<TFieldValues> = {}
) => {
  const data = useLicenseStore(state => state.data)
  const setData = useLicenseStore(state => state.setData)

  const form = useForm<TFieldValues>({
    mode: "onChange",
    ...options,
    defaultValues: { ...(options.defaultValues ?? {}), ...(data as TFieldValues) }
  })

  const lastSynced = useRef(serialize({ ...(data as TFieldValues) }))
  const isResetting = useRef(false)

  useEffect(() => {
    const subscription = form.watch(values => {
      if (isResetting.current) return
      const serialized = serialize(values)

      lastSynced.current = serialized
      setData(values as TFieldValues)
    })

    return () => subscription.unsubscribe()
  }, [form, setData])

  const resetForm = useCallback(
    (nextValues: TFieldValues = {} as TFieldValues) => {
      const currentValues = form.getValues()
      const blankValues = Object.fromEntries(
        Object.entries(currentValues).map(([key, value]) => {
          if (Array.isArray(value)) return [key, []]
          if (value && typeof value === "object") return [key, {}]
          if (typeof value === "boolean") return [key, false]
          return [key, ""]
        })
      ) as TFieldValues
      const resetValues = { ...blankValues, ...nextValues }
      const serialized = serialize(resetValues)
      isResetting.current = true
      form.reset(resetValues)
      form.clearErrors()
      lastSynced.current = serialized
      setData(resetValues)
      isResetting.current = false
    },
    [form, setData]
  )

  useEffect(() => {
    const mergedDefaults = { ...(options.defaultValues ?? {}), ...(data as TFieldValues) }
    const serializedDefaults = serialize(mergedDefaults)

    if (serializedDefaults !== lastSynced.current) {
      isResetting.current = true
      form.reset(mergedDefaults)
      lastSynced.current = serializedDefaults
      isResetting.current = false
    }
  }, [data, form, options.defaultValues])

  return { form, resetForm }
}
